// Mobile utility functions and responsive helpers

export const isMobile = () => {
  return window.innerWidth < 768;
};

export const isTablet = () => {
  return window.innerWidth >= 768 && window.innerWidth < 1024;
};

export const isDesktop = () => {
  return window.innerWidth >= 1024;
};

// Mobile-friendly responsive classes
export const mobileClasses = {
  // Container classes
  container: 'px-4 sm:px-6 lg:px-8',
  containerSmall: 'px-2 sm:px-4 lg:px-6',
  
  // Grid classes
  grid1: 'grid grid-cols-1',
  grid2: 'grid grid-cols-1 sm:grid-cols-2',
  grid3: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  grid4: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  
  // Text classes
  textMobile: 'text-sm sm:text-base',
  textMobileLarge: 'text-base sm:text-lg',
  textMobileXLarge: 'text-lg sm:text-xl lg:text-2xl',
  
  // Button classes
  buttonMobile: 'w-full sm:w-auto px-4 py-2 text-sm',
  buttonMobileLarge: 'w-full sm:w-auto px-6 py-3 text-base',
  
  // Card classes
  cardMobile: 'p-4 sm:p-6',
  cardMobileSmall: 'p-3 sm:p-4',
  
  // Form classes
  formMobile: 'space-y-4 sm:space-y-6',
  inputMobile: 'w-full px-3 py-2 text-sm sm:text-base',
  
  // Modal classes
  modalMobile: 'w-11/12 sm:w-3/4 lg:w-1/2 xl:w-1/3',
  modalMobileFull: 'w-11/12 sm:w-3/4 lg:w-2/3',
  
  // Table classes
  tableMobile: 'overflow-x-auto',
  tableCellMobile: 'px-2 sm:px-4 py-2',
  
  // Navigation classes
  navMobile: 'flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4',
  
  // Spacing classes
  spacingMobile: 'space-y-4 sm:space-y-6',
  spacingMobileSmall: 'space-y-2 sm:space-y-4',
  
  // Flex classes
  flexMobile: 'flex flex-col sm:flex-row',
  flexMobileReverse: 'flex flex-col-reverse sm:flex-row',
  
  // Hidden classes
  hiddenMobile: 'hidden sm:block',
  visibleMobile: 'block sm:hidden',
  hiddenTablet: 'hidden md:block',
  visibleTablet: 'block md:hidden'
};

// Mobile-friendly breakpoints
export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px'
};

// Touch-friendly sizes
export const touchSizes = {
  button: 'min-h-[44px] min-w-[44px]', // iOS recommended minimum touch target
  input: 'min-h-[44px]',
  link: 'min-h-[44px] min-w-[44px]',
  icon: 'h-6 w-6 sm:h-5 sm:w-5'
};

// Mobile-specific utility functions
export const formatForMobile = {
  // Format text for mobile display
  truncateText: (text: string, maxLength: number = 50) => {
    if (isMobile() && text.length > maxLength) {
      return text.substring(0, maxLength) + '...';
    }
    return text;
  },
  
  // Format date for mobile
  formatDate: (date: string | Date) => {
    const d = new Date(date);
    if (isMobile()) {
      return d.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  },
  
  // Format phone number for mobile
  formatPhone: (phone: string) => {
    if (isMobile()) {
      // Show clickable phone link on mobile
      return phone.replace(/\D/g, '').length === 10 
        ? `tel:+91${phone.replace(/\D/g, '')}`
        : phone;
    }
    return phone;
  }
};

// Mobile navigation helpers
export const mobileNav = {
  // Check if current route is active
  isActiveRoute: (currentPath: string, routePath: string) => {
    return currentPath === routePath;
  },
  
  // Get mobile-friendly route name
  getMobileRouteName: (routeName: string) => {
    const mobileNames: { [key: string]: string } = {
      'Dashboard': 'Home',
      'Complaints': 'Issues',
      'Events': 'Events',
      'Business': 'Business',
      'Charges': 'Fees',
      'Elections': 'Vote',
      'Profile': 'Profile',
      'Members': 'Members',
      'Staff': 'Staff',
      'Departments': 'Depts',
      'Categories': 'Categories',
      'CMS': 'Content',
      'Reports': 'Reports'
    };
    
    return isMobile() ? (mobileNames[routeName] || routeName) : routeName;
  }
};

// Mobile form helpers
export const mobileForm = {
  // Get appropriate input size
  getInputSize: () => {
    return isMobile() ? 'lg' : 'md';
  },
  
  // Get appropriate button size
  getButtonSize: () => {
    return isMobile() ? 'lg' : 'md';
  },
  
  // Check if form should be full width
  shouldBeFullWidth: () => {
    return isMobile();
  }
};

// Mobile table helpers
export const mobileTable = {
  // Get table classes for mobile
  getTableClasses: () => {
    return isMobile() 
      ? 'min-w-full divide-y divide-gray-200 text-sm'
      : 'min-w-full divide-y divide-gray-200';
  },
  
  // Get cell classes for mobile
  getCellClasses: () => {
    return isMobile()
      ? 'px-2 py-2 whitespace-nowrap text-xs'
      : 'px-6 py-4 whitespace-nowrap text-sm';
  },
  
  // Check if table should be scrollable
  shouldBeScrollable: () => {
    return isMobile();
  }
};

// Mobile modal helpers
export const mobileModal = {
  // Get modal size classes
  getSizeClasses: (size: 'sm' | 'md' | 'lg' | 'xl' | 'full' = 'md') => {
    const sizes = {
      sm: 'w-11/12 sm:w-1/2 lg:w-1/3',
      md: 'w-11/12 sm:w-3/4 lg:w-1/2',
      lg: 'w-11/12 sm:w-3/4 lg:w-2/3',
      xl: 'w-11/12 sm:w-4/5 lg:w-3/4',
      full: 'w-11/12 sm:w-full'
    };
    
    return sizes[size];
  },
  
  // Get modal position classes
  getPositionClasses: () => {
    return isMobile() 
      ? 'fixed inset-0 z-50 overflow-y-auto'
      : 'fixed inset-0 z-50 overflow-y-auto';
  }
};

// Mobile card helpers
export const mobileCard = {
  // Get card padding classes
  getPaddingClasses: (size: 'sm' | 'md' | 'lg' = 'md') => {
    const paddings = {
      sm: 'p-3 sm:p-4',
      md: 'p-4 sm:p-6',
      lg: 'p-6 sm:p-8'
    };
    
    return paddings[size];
  },
  
  // Get card spacing classes
  getSpacingClasses: () => {
    return isMobile() ? 'space-y-3' : 'space-y-4';
  }
};

// Mobile-specific constants
export const mobileConstants = {
  // Touch target minimum size (iOS guidelines)
  MIN_TOUCH_TARGET: 44,
  
  // Maximum content width for readability
  MAX_CONTENT_WIDTH: '768px',
  
  // Mobile-friendly font sizes
  FONT_SIZES: {
    xs: 'text-xs',
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
    '3xl': 'text-3xl'
  },
  
  // Mobile-friendly spacing
  SPACING: {
    xs: 'space-y-1',
    sm: 'space-y-2',
    md: 'space-y-4',
    lg: 'space-y-6',
    xl: 'space-y-8'
  }
};

export default {
  isMobile,
  isTablet,
  isDesktop,
  mobileClasses,
  breakpoints,
  touchSizes,
  formatForMobile,
  mobileNav,
  mobileForm,
  mobileTable,
  mobileModal,
  mobileCard,
  mobileConstants
};
